import { supabasePublic } from "../../../config/supabaseClient";
import { queryClient } from "../../../Utils/query/queryClient";
import { getTemplateComponents } from "../../../Utils/HelperFunction/HelperFunction";
import { resolveSubscriptionEntitlements } from "../../../Utils/subscriptionPlan";
import type { CreatorData, Tweaks } from "./types";
import {
  FALLBACK_TEMPLATE_TWEAKS,
  mapTemplateSettingsRow,
} from "./templateSettingsMap";
import { publishLiveWebsiteLoaderTweaks } from "./websiteTemplateTweaksCache";
import { preloadTemplateLcpImage } from "../../../Utils/templateImageDelivery";
import type { CreatorPublicSurface } from "../../../Utils/url/creatorPublicSurface";

/** Columns needed to paint the public site shell — omit unused jsonb blobs. */
export const WEBSITE_BOOTSTRAP_TEMPLATE_COLS =
  "tweaks,bio,highlights,links,banners,carousels,products,press,about,recent";

/** Shared with useCreatorDataFromDB so bootstrap can warm phase-1a. */
export function websiteTemplateComponentsQueryKey(userId: string) {
  return ["websiteTemplateComponents", "v1", userId.trim()] as const;
}

const TEMPLATE_COMPONENTS_STALE_MS = 30_000;

/**
 * Warm `template_components` as soon as bootstrap knows userId (parallel with
 * template_settings). Phase 1a then hits this cache instead of another Slow 4G RTT.
 */
export function prefetchWebsiteTemplateComponents(
  username: string,
  userId: string,
): void {
  const uid = userId.trim();
  const u = username.trim().toLowerCase();
  if (!uid || !u) return;
  void queryClient.prefetchQuery(
    websiteTemplateComponentsQueryKey(uid),
    () => getTemplateComponents(u, supabasePublic, uid),
    { staleTime: TEMPLATE_COMPONENTS_STALE_MS },
  );
}

export function fetchWebsiteTemplateComponentsCached(
  username: string,
  userId: string,
): ReturnType<typeof getTemplateComponents> {
  const uid = userId.trim();
  const u = username.trim().toLowerCase();
  if (!uid || !u) {
    return Promise.resolve({
      order: [],
      data: [],
      showingComponents: [],
      archiveComponents: [],
    });
  }
  return queryClient.fetchQuery(
    websiteTemplateComponentsQueryKey(uid),
    () => getTemplateComponents(u, supabasePublic, uid),
    { staleTime: TEMPLATE_COMPONENTS_STALE_MS },
  );
}

/** Public `/{username}` homes. Dashboard and `/website/:user` do not set this. */
const publicHomeIntent = new Set<string>();
let preloadPublicHome:
  | ((username: string, surface: CreatorPublicSurface) => void)
  | null = null;

export function markCreatorPublicHomeIntent(username: string): void {
  const key = username.trim().toLowerCase();
  if (key) publicHomeIntent.add(key);
}

/** Registered by the public route so bootstrap can start its chunk without a circular import. */
export function registerCreatorPublicHomePreload(
  preload: (username: string, surface: CreatorPublicSurface) => void,
): void {
  preloadPublicHome = preload;
}

function notifyCreatorPublicHome(
  username: string,
  surface: CreatorPublicSurface,
): void {
  if (!publicHomeIntent.has(username)) return;
  preloadPublicHome?.(username, surface);
}

type LiveUserRowResult = { data: Record<string, unknown>[] | null };
const LIVE_USER_ROW_MAX_AGE_MS = 30_000;
const prefetchedLiveUserRows = new Map<
  string,
  { at: number; pending: Promise<LiveUserRowResult> }
>();

/**
 * Start phase-1a `user.select(*)` alongside template_settings so the public
 * home does not wait for it after the route chunk loads.
 */
function prefetchWebsiteLiveUserRow(userId: string): void {
  const uid = userId.trim();
  if (!uid || prefetchedLiveUserRows.has(uid)) return;
  const pending = Promise.resolve(
    supabasePublic.from("user").select("*").eq("user_id", uid).limit(1),
  ).then((res) => {
    if (res.error) throw res.error;
    return { data: res.data as Record<string, unknown>[] | null };
  });
  pending.catch(() => prefetchedLiveUserRows.delete(uid));
  prefetchedLiveUserRows.set(uid, { at: Date.now(), pending });
}

/** One-shot: later refreshes must hit the network for fresh data. */
export function takePrefetchedWebsiteLiveUserRow(
  userId: string,
): Promise<LiveUserRowResult> | null {
  const uid = userId.trim();
  const entry = prefetchedLiveUserRows.get(uid);
  if (!entry) return null;
  prefetchedLiveUserRows.delete(uid);
  return Date.now() - entry.at <= LIVE_USER_ROW_MAX_AGE_MS ? entry.pending : null;
}

export type WebsiteTemplateBootstrapResult =
  | { userNotFound: true }
  | {
      userNotFound: false;
      /** True when the creator has never hit Publish — public URL is gated. */
      unpublished: boolean;
      /**
       * Dashboard Website editor access (`hasPages`): full Website, Website Demo,
       * or Growth onboarding fallback.
       */
      hasWebsiteProductAccess: boolean;
      /** Full Website (or onboarding fallback) — may go live at `/{username}`. */
      canPublishWebsite: boolean;
      /** Admin `website_demo` — only true when full Website cannot Publish. */
      hasWebsiteDemo: boolean;
      /**
       * Public `/{username}` serves the website template only when the site is
       * published AND the creator can Publish (full Website). Demo-only never
       * flips the canonical URL — use `/website/:username` instead.
       */
      hasWebsiteSurface: boolean;
      /**
       * `/website/:username` may render the draft website (Demo, or full Website
       * while unpublished / published).
       */
      canServeWebsiteDemoPath: boolean;
      userId: string;
      tweaks: Tweaks;
      creatorData: Partial<CreatorData>;
    };

function success(
  userId: string,
  row: Record<string, unknown> | null | undefined,
  unpublished: boolean,
  hasWebsiteProductAccess: boolean,
  canPublishWebsite: boolean,
  hasWebsiteDemo: boolean,
  hasWebsiteSurface: boolean,
  canServeWebsiteDemoPath: boolean,
): WebsiteTemplateBootstrapResult {
  const mapped = row
    ? mapTemplateSettingsRow(row)
    : { tweaks: FALLBACK_TEMPLATE_TWEAKS, creatorData: {} as Partial<CreatorData> };
  return {
    userNotFound: false,
    unpublished,
    hasWebsiteProductAccess,
    canPublishWebsite,
    hasWebsiteDemo,
    hasWebsiteSurface,
    canServeWebsiteDemoPath,
    userId,
    tweaks: mapped.tweaks,
    creatorData: mapped.creatorData,
  };
}

/**
 * Fast `/website/:username` bootstrap — resolve `user_id`, then `template_settings`.
 *
 * Note: a PostgREST embed (`template_settings` ⨝ `user`) is not available — there is
 * no FK relationship in the schema cache — so we always use two minimal queries.
 *
 * Does **not** honor RQ AbortSignal / Supabase `abortSignal`. Prefetch + remount
 * races were randomly cancelling this read (AbortError code 20), which cleared
 * `isError` in our useQuery wrapper and left the public site on permanent
 * skeletons. These two queries are tiny — always let them finish so the cache
 * warms for the next observer.
 */
export async function fetchWebsiteTemplateBootstrap(
  username: string,
  _signal?: AbortSignal,
): Promise<WebsiteTemplateBootstrapResult> {
  const u = username.trim().toLowerCase();
  if (!u) return { userNotFound: true };

  const { data: userRow, error: userError } = await supabasePublic
    .from("user")
    .select(
      "user_id, browsable_website_published_at, website_demo, stripe_detail, subscription_type, payment_plan, payment_status, about_image, picture",
    )
    .eq("username", u)
    .maybeSingle();

  if (userError) throw userError;

  const userId = userRow?.user_id != null ? String(userRow.user_id).trim() : "";
  if (!userId) {
    return { userNotFound: true };
  }

  const publishedAtUser = String(
    (userRow as { browsable_website_published_at?: string | null })
      ?.browsable_website_published_at || "",
  ).trim();
  const entitlements = resolveSubscriptionEntitlements(userRow);
  const hasWebsiteProductAccess = entitlements.hasPages;
  const canPublishWebsite = entitlements.canPublishWebsite;
  const hasWebsiteDemo = entitlements.hasWebsiteDemo;

  // No Website product: the public surface can only be Link-in-Bio, so skip
  // template_settings / template_components and the website LCP preload.
  if (!hasWebsiteProductAccess) {
    notifyCreatorPublicHome(u, "link-in-bio");
    return success(userId, null, !publishedAtUser, false, false, false, false, false);
  }

  const earlyLcp =
    String((userRow as { about_image?: string | null })?.about_image || "").trim() ||
    String((userRow as { picture?: string | null })?.picture || "").trim();
  // Kick LCP download as soon as we have a profile/about URL (before settings),
  // except on the public home: that image competes with the page file, and the
  // measured paint there is the template heading.
  if (earlyLcp && !publicHomeIntent.has(u)) {
    preloadTemplateLcpImage(earlyLcp);
  }

  // Settings unlock the heading. Start that read before the heavier fan-out.
  const settingsPromise = supabasePublic
    .from("template_settings")
    .select(WEBSITE_BOOTSTRAP_TEMPLATE_COLS)
    .eq("user_id", userId)
    .maybeSingle();

  const homeFirst =
    canPublishWebsite && publishedAtUser && publicHomeIntent.has(u);
  if (homeFirst) {
    notifyCreatorPublicHome(u, "website");
  } else {
    // Editor and nested routes can overlap these with settings.
    prefetchWebsiteTemplateComponents(u, userId);
  }

  const { data: settingsRow, error: settingsError } = await settingsPromise;

  if (homeFirst) {
    prefetchWebsiteTemplateComponents(u, userId);
    prefetchWebsiteLiveUserRow(userId);
  }

  const finish = (
    row: Record<string, unknown> | null | undefined,
    unpublished: boolean,
  ) => {
    // Live home: published + full Website only (never admin Demo-only).
    const hasWebsiteSurface = !unpublished && canPublishWebsite;
    // `/website/:user`: Demo flag or any Website-editor account (draft or live).
    const canServeWebsiteDemoPath = hasWebsiteProductAccess;
    return success(
      userId,
      row,
      unpublished,
      hasWebsiteProductAccess,
      canPublishWebsite,
      hasWebsiteDemo,
      hasWebsiteSurface,
      canServeWebsiteDemoPath,
    );
  };

  const reportSurface = (result: WebsiteTemplateBootstrapResult) => {
    if (result.userNotFound) return;
    notifyCreatorPublicHome(u, result.hasWebsiteSurface ? "website" : "link-in-bio");
  };

  if (settingsError) {
    const unpublished = !publishedAtUser;
    const result = finish(null, unpublished);
    publishLiveWebsiteLoaderTweaks(u, result.tweaks);
    reportSurface(result);
    return result;
  }

  const publishedAtTweaks = String(
    (settingsRow as { tweaks?: { gettingStarted?: { publishedAt?: string | null } } } | null)
      ?.tweaks?.gettingStarted?.publishedAt || "",
  ).trim();
  const unpublished = !publishedAtUser && !publishedAtTweaks;
  const result = finish(settingsRow ?? null, unpublished);
  // One bootstrap round-trip also paints the @handle loader palette —
  // do not fire a parallel user + tweaks-only pair (was doubling early APIs).
  publishLiveWebsiteLoaderTweaks(u, result.tweaks);
  reportSurface(result);
  // Prefer template-saved about/hero if present (often the real LCP).
  if (!result.userNotFound) {
    const about = result.creatorData.about as { headshot?: string } | undefined;
    const bio = result.creatorData.bio as { avatar?: string } | undefined;
    const fromSettings =
      String(about?.headshot || "").trim() ||
      String(bio?.avatar || "").trim();
    if (fromSettings) preloadTemplateLcpImage(fromSettings);
  }
  return result;
}

export function websiteTemplateBootstrapQueryKey(username: string) {
  // v4: hasWebsiteSurface = published AND canPublishWebsite (Demo never flips /:user).
  return ["websiteTemplateBootstrap", "v4", username] as const;
}

/** Match MasterTemplateContentContext so prefetch + surface detect share one flight. */
const BOOTSTRAP_STALE_MS = 30_000;

/**
 * Warms the react-query cache for the bootstrap query.
 * Safe to call multiple times — react-query deduplicates in-flight requests.
 */
export function prefetchWebsiteTemplateBootstrap(username: string): void {
  const key = username.trim().toLowerCase();
  if (!key) return;
  queryClient.prefetchQuery(
    websiteTemplateBootstrapQueryKey(key),
    () => fetchWebsiteTemplateBootstrap(key),
    { staleTime: BOOTSTRAP_STALE_MS },
  );
}

/**
 * Resolve bootstrap via the shared RQ cache (used by Routing surface detect).
 * Prefer this over calling `fetchWebsiteTemplateBootstrap` directly so we do
 * not race a second network pair alongside prefetch.
 */
export function fetchWebsiteTemplateBootstrapCached(
  username: string,
): Promise<WebsiteTemplateBootstrapResult> {
  const key = username.trim().toLowerCase();
  if (!key) return Promise.resolve({ userNotFound: true });
  return queryClient.fetchQuery(
    websiteTemplateBootstrapQueryKey(key),
    () => fetchWebsiteTemplateBootstrap(key),
    { staleTime: BOOTSTRAP_STALE_MS },
  );
}
